import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bug, Moon, Wind, SprayCan, Flame, Droplets, ArrowLeft, Phone, CalendarCheck, RotateCcw } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { CALENDLY_URL } from "@/data/site";

const SURFACES = ["0 à 30 m²", "31 à 60 m²", "61 à 90 m²", "91 à 120 m²", "121 à 150 m²", "151 à 180 m²", "181 m² et plus"];

const PESTS = {
    punaises: {
        label: "Punaises de lit",
        icon: Moon,
        methods: [
            { id: "vapeur", label: "Vapeur sèche 180°C", hint: "100% naturelle, 1 seul passage", icon: Wind, prices: [350, 400, 450, 500, 550, 600, null] },
            { id: "chimique", label: "Traitement chimique", hint: "Insecticides pro, 2 passages", icon: SprayCan, prices: [180, 190, 210, 230, 250, 270, null] },
            { id: "fumigene", label: "Fumigène effet choc", hint: "Longue rémanence", icon: Flame, flat: 160 },
        ],
    },
    cafards: {
        label: "Cafards & blattes",
        icon: Bug,
        methods: [
            { id: "gel", label: "Gel professionnel", hint: "Cuisine, salle d'eau, WC", icon: Droplets, flat: 59 },
            { id: "chimique", label: "Pulvérisation chimique", hint: "Éradication rapide", icon: SprayCan, prices: [170, 190, 210, 230, 250, null] },
        ],
    },
};

const EstimateurSection = ({ dark = false }) => {
    const [pest, setPest] = useState(null);
    const [method, setMethod] = useState(null);
    const [surface, setSurface] = useState(null);

    const surfacesFor = (m) => (m?.prices ? SURFACES.slice(0, m.prices.length) : []);
    const price = method?.flat ?? (method && surface !== null ? method.prices[surface] : null);
    const showResult = method && (method.flat !== undefined || surface !== null);
    const step = !pest ? 1 : !method ? 2 : method.flat !== undefined || surface !== null ? 4 : 3;

    const reset = () => {
        setPest(null);
        setMethod(null);
        setSurface(null);
    };

    const OptionButton = ({ testid, onClick, icon: Icon, label, hint }) => (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className="group flex w-full items-center gap-4 rounded-2xl border border-black/10 bg-white p-4 text-left shadow-sm transition-[border-color,box-shadow,transform] duration-300 hover:border-brand hover:shadow-md hover:scale-[1.01] active:scale-[0.99]"
        >
            {Icon && (
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                    <Icon className="h-5 w-5" />
                </span>
            )}
            <span>
                <span className="block font-bold text-night">{label}</span>
                {hint && <span className="block text-xs text-slate-500">{hint}</span>}
            </span>
        </button>
    );

    return (
        <section className={`${dark ? "bg-mist" : "bg-white"} py-20 md:py-24`} data-testid="estimateur-section">
            <div className="mx-auto max-w-3xl px-5 sm:px-8">
                <Reveal className="text-center"><SectionTag>Estimation instantanée</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 text-center font-display text-3xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Votre prix en <span className="text-brand">3 clics</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mx-auto mt-4 max-w-xl text-center text-sm leading-relaxed text-slate-600 sm:text-base">
                        Le prix affiché est le prix facturé : pas de surprise. Estimation indicative, confirmée avant intervention.
                    </p>
                </Reveal>

                <Reveal delay={0.2} className="mt-10">
                    <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-[0_20px_60px_rgba(10,15,36,0.08)] sm:p-10">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                                {showResult ? "Votre estimation" : `Étape ${step}/3`}
                            </span>
                            {pest && (
                                <button type="button" data-testid="estimateur-restart" onClick={reset} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-brand">
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Recommencer
                                </button>
                            )}
                        </div>

                        <AnimatePresence mode="wait">
                            {!pest && (
                                <motion.div key="pest" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }} className="mt-6 space-y-3">
                                    <p className="font-display text-lg font-black uppercase tracking-tight">Quel nuisible ?</p>
                                    {Object.entries(PESTS).map(([id, p]) => (
                                        <OptionButton key={id} testid={`estimateur-pest-${id}`} onClick={() => setPest(id)} icon={p.icon} label={p.label} />
                                    ))}
                                </motion.div>
                            )}

                            {pest && !method && (
                                <motion.div key="method" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }} className="mt-6 space-y-3">
                                    <p className="font-display text-lg font-black uppercase tracking-tight">Quelle méthode ?</p>
                                    {PESTS[pest].methods.map((m) => (
                                        <OptionButton key={m.id} testid={`estimateur-method-${m.id}`} onClick={() => setMethod(m)} icon={m.icon} label={m.label} hint={m.hint} />
                                    ))}
                                </motion.div>
                            )}

                            {pest && method && method.prices && surface === null && (
                                <motion.div key="surface" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }} className="mt-6">
                                    <p className="font-display text-lg font-black uppercase tracking-tight">Superficie du logement ?</p>
                                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                                        {surfacesFor(method).map((s, i) => (
                                            <button
                                                key={s}
                                                type="button"
                                                data-testid={`estimateur-surface-${i}`}
                                                onClick={() => setSurface(i)}
                                                className="rounded-2xl border border-black/10 bg-white px-4 py-3.5 text-sm font-bold text-night shadow-sm transition-[border-color,transform] duration-300 hover:border-brand hover:scale-[1.03] active:scale-95"
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {showResult && (
                                <motion.div key="result" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="mt-6" data-testid="estimateur-result">
                                    <div className="rounded-2xl bg-brand p-6 text-center text-white sm:p-8">
                                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/75">
                                            {PESTS[pest].label} · {method.label}{surface !== null ? ` · ${surfacesFor(method)[surface]}` : ""}
                                        </p>
                                        <p data-testid="estimateur-price" className="mt-3 font-display text-5xl font-black sm:text-6xl">
                                            {price === null ? "Sur devis" : `${price} €`}
                                        </p>
                                        <p className="mt-2 text-sm text-white/80">{price === null ? "Votre configuration demande une étude personnalisée." : "HT, TVA non applicable (art. 293 B du CGI)"}</p>
                                    </div>
                                    {pest === "punaises" && price !== null && (
                                        <p className="mt-4 text-center text-sm font-semibold text-brand">
                                            La recherche (59 €) est déduite si le traitement est réalisé le jour même.
                                        </p>
                                    )}
                                    <p className="mt-2 text-center text-xs text-slate-500">Majoration de 20 % les samedis, dimanches et jours fériés.</p>
                                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                                        <a
                                            data-testid="estimateur-booking-button"
                                            href={CALENDLY_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                                        >
                                            <CalendarCheck className="h-5 w-5" />
                                            Réserver ce créneau
                                        </a>
                                        <a
                                            data-testid="estimateur-phone-button"
                                            href="tel:+33255992243"
                                            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                                        >
                                            <Phone className="h-5 w-5 text-brand" />
                                            02 55 99 22 43
                                        </a>
                                    </div>
                                    <p className="mt-4 text-center text-xs text-slate-500">
                                        Un cas spécifique ?{" "}
                                        <Link data-testid="estimateur-contact-link" to="/contact" className="font-semibold text-brand underline">
                                            Décrivez votre situation
                                        </Link>
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {(method || surface !== null) && !showResult && (
                            <button
                                type="button"
                                data-testid="estimateur-back"
                                onClick={() => (surface !== null ? setSurface(null) : setMethod(null))}
                                className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-brand"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                Retour
                            </button>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default EstimateurSection;
